import React from "react";
import { validatePhotoSelection } from "../../utils/uploadValidation";
import { STEP_TITLES_BY_MODE } from "./spotFormConstants";

export function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

export function distanceMeters(lat1, lng1, lat2, lng2) {
    const r = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;

    return 2 * r * Math.atan2(Math.sqrt(a), Math.sqrt(Math.max(0, 1 - a)));
}

export function fieldError(message) {
    return message ? <span className="new-spot-field-error">{message}</span> : null;
}

export function validateRoute(drawPoints, lat, lng) {
    if (!drawPoints?.length) {
        return "";
    }
    if (drawPoints.length < 2) {
        return "Маршрут должен содержать минимум две точки.";
    }

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return "У точки не заданы координаты.";
    }

    const first = drawPoints[0];
    const last = drawPoints[drawPoints.length - 1];
    const firstDist = distanceMeters(lat, lng, first[0], first[1]);
    const lastDist = distanceMeters(lat, lng, last[0], last[1]);
    if (Math.min(firstDist, lastDist) > 100) {
        return "Начало или конец маршрута должен быть в пределах 100 м от точки.";
    }

    const tooFar = drawPoints.some(([pointLat, pointLng]) => distanceMeters(lat, lng, pointLat, pointLng) > 10000);
    if (tooFar) {
        return "Все точки маршрута должны быть в пределах 10 км от точки.";
    }

    return "";
}

export function validateGeneralFields(form, { mode = "create", lat, lng } = {}, reportFormOpen = false, reportFileErrors = {}) {
    const errors = {};

    if (mode === "create") {
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            errors.coordinates = "Сначала выберите точку на карте.";
        }
        if (!form.title?.trim()) {
            errors.title = "Укажите название точки.";
        } else if (form.title.trim().length > 140) {
            errors.title = "Название должно быть не длиннее 140 символов.";
        }
    }

    if (!form.description?.trim()) {
        errors.description = "Заполните описание точки.";
    }
    if (!form.bankType) {
        errors.bankType = "Выберите тип берега.";
    }

    const capacity = Number(form.capacityPersons);
    if (!Number.isInteger(capacity) || capacity < 1 || capacity > 20) {
        errors.capacityPersons = "Укажите число от 1 до 20.";
    }

    if (form.maxDepth === "") {
        errors.maxDepth = "Укажите максимальную глубину.";
    } else {
        const maxDepth = Number(form.maxDepth);
        if (!Number.isFinite(maxDepth) || maxDepth < 0 || maxDepth > 100) {
            errors.maxDepth = "Укажите глубину от 0 до 100 м.";
        }
    }

    if (reportFormOpen) {
        const now = new Date();
        const start = form.startTime ? new Date(form.startTime) : null;
        const end = form.endTime ? new Date(form.endTime) : null;

        if (!start || Number.isNaN(start.getTime())) {
            errors.startTime = "Дата начала рыбалки указана некорректно.";
        } else if (start && start > now) {
            errors.startTime = "Дата начала рыбалки не может быть позже текущей даты.";
        }

        if (!end || Number.isNaN(end.getTime())) {
            errors.endTime = "Дата окончания рыбалки указана некорректно.";
        } else if (end && end > now) {
            errors.endTime = "Дата окончания рыбалки не может быть позже текущей даты.";
        }

        if (!errors.startTime && !errors.endTime && start && end && start > end) {
            errors.startTime = "Дата начала рыбалки не может быть позже даты окончания.";
            errors.endTime = "Дата окончания должна быть не раньше даты начала.";
        }

        const fishingType = form.fishingType;
        const fishingTypes = Array.isArray(fishingType)
            ? fishingType.filter((item) => item && String(item) !== "0")
            : (fishingType && fishingType !== "0" ? [fishingType] : []);
        if (!fishingTypes.length) {
            errors.fishingType = 'Введите тип ловли.';
        }

        const depthText = String(form.depth || "").trim();
        if (depthText !== "") {
            const depth = Number(depthText);
            if (!Number.isFinite(depth) || depth < 0 || depth > 100) {
                errors.depth = "Глубина должна быть от 0 до 100.";
            }
        } else {
            errors.depth = "Глубина должна быть от 0 до 100.";
        }

        const report = String(form.report || "").trim();
        if (report === "") {
            errors.report = "Введите текст отчёта.";
        }
    }

    return errors;
}

export function validatePhotoFiles(files, fileErrors = {}) {
    const nextFileErrors = {};

    Object.entries(fileErrors).forEach(([type, error]) => {
        if (error) {
            nextFileErrors[type] = error;
        }
    });

    Object.entries(files || {}).forEach(([type, fileList]) => {
        const result = validatePhotoSelection(type, fileList);
        if (result.error) {
            nextFileErrors[type] = result.error;
        }
    });

    return nextFileErrors;
}

export function validateStep({
    mode = "create",
    currentStep,
    form,
    files,
    fileErrors = {},
    drawPoints = [],
    lat,
    lng,
    placeInfo,
}) {
    const stepTitle = STEP_TITLES_BY_MODE[mode]?.[currentStep];
    const nextErrors = {};
    let nextFileErrors = {};
    let nextRouteError = "";

    if (stepTitle === "Локация") {
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            nextErrors.coordinates = "Сначала выберите точку на карте.";
        } else if (!placeInfo) {
            nextErrors.coordinates = "Дождитесь определения водоёма.";
        }
    }

    if (stepTitle === "Общая информация") {
        Object.assign(nextErrors, validateGeneralFields(form, { mode, lat, lng }));
    }

    if (stepTitle === "Фото") {
        nextFileErrors = validatePhotoFiles(files, fileErrors);
    }

    if (stepTitle === "Нарисовать маршрут" && Number.isFinite(lat) && Number.isFinite(lng)) {
        nextRouteError = validateRoute(drawPoints, lat, lng);
    }

    const isValid = Object.keys(nextErrors).length === 0
        && Object.keys(nextFileErrors).length === 0
        && !nextRouteError;

    return {
        fieldErrors: nextErrors,
        fileErrors: nextFileErrors,
        routeError: nextRouteError,
        isValid,
    };
}

export function validateBeforeSubmit({
    mode = "create",
    form,
    files,
    fileErrors = {},
    drawPoints = [],
    lat,
    lng,
    reportFormOpen,
    reportFileErrors
}) {
    const fieldErrors = validateGeneralFields(form, { mode, lat, lng }, reportFormOpen, reportFileErrors);
    const nextFileErrors = validatePhotoFiles(files, fileErrors);
    const nextRouteError = Number.isFinite(lat) && Number.isFinite(lng)
        ? validateRoute(drawPoints, lat, lng)
        : (drawPoints?.length ? "У точки не заданы координаты." : "");

    const isValid = Object.keys(fieldErrors).length === 0
        && Object.keys(nextFileErrors).length === 0
        && !nextRouteError;

    return {
        fieldErrors,
        fileErrors: nextFileErrors,
        routeError: nextRouteError,
        isValid,
    };
}
